import { useCallback } from 'react';

import { useUser } from '@/hooks/useUser';

import { useCompanySearch } from './useCompanySearch';

import { getTickerCellTemplate, getCurrencyISO } from '../../CustomCell/Ticker';

import { getBestMatchedSearchListing } from '../../../utils/getBestMatchedSearchListing';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useTickerCellSearch = () => {
  const { data: user } = useUser();
  const { searchCompany } = useCompanySearch();

  const search = useCallback(
    async (query: string) => {
      const [searchResults] = await Promise.all([
        searchCompany(query),
        // Here we simulate a delay of at least 250ms to show the pending state
        // Algolia is fast and responds in ~200ms on avg
        // Without this delay, the cell will just flash the pending state
        wait(250),
      ]);
      const [firstCompany] = searchResults;

      const listing = getBestMatchedSearchListing({
        query,
        company: firstCompany,
        countryISO: user.defaultMarketISO,
      });

      if (!listing) {
        return getTickerCellTemplate({
          value: query,
          status: 'error',
          error: "Can't match to a company",
        });
      }

      const { exchangeCountryIso, name, uniqueSymbol } = listing;

      return getTickerCellTemplate({
        value: query,
        currencyISO: getCurrencyISO(exchangeCountryIso),
        name: name,
        uniqueSymbol: uniqueSymbol,
        status: 'success',
      });
    },
    [searchCompany, user.defaultMarketISO]
  );

  return { search };
};
